import { z } from "zod";

/** M9 follow-up "which AI did this" (DECISIONS 2026-10-05): every note, draft and change a member's own AI makes says which app and which
 *  model did it, "Kriesi's AI · Claude Code · Opus 5.5". Two sources, never guessed: the **app** from our side (the sign-in's client
 *  document, the protocol handshake's `clientInfo`), and the **model** as the AI reports it itself (an optional `model` on the tools that
 *  write). A part nobody reported is left out. The one place the label's rule lives; the database's `agent_connections_client_app_check`
 *  mirrors `AI_APPS`. Pure. */

/** the member's AI, by the member's name, in the words the screens use (M9 Q4: "<name>'s AI"); moved here from agentTools with the marks */
export const aiNameOf = (name: string) => `${name}'s AI`;

/** The apps we can tell apart. `claude` is Claude's hosted apps when we cannot tell which one (the web, the desktop app and Cowork sign in
 *  with the same client document, and Anthropic's connector names itself `claude-ai`); the three specific ids are for a handshake that
 *  tells them apart. The "Your own AI" round adds the other agents (ChatGPT, Gemini CLI, Cursor, Codex, VS Code, and Grok for the marks
 *  only: it is not in the picker). `other` is any other client: recorded, never named on screen. */
export const AI_APPS = ["claude", "claude-web", "claude-desktop", "cowork", "claude-code", "chatgpt", "gemini-cli", "cursor", "codex", "vscode", "grok", "other"] as const;
export type AiApp = (typeof AI_APPS)[number];

const APP_WORDS: Record<Exclude<AiApp, "other">, string> = {
  claude: "Claude", "claude-web": "Claude on the web", "claude-desktop": "Claude desktop", cowork: "Cowork", "claude-code": "Claude Code",
  chatgpt: "ChatGPT", "gemini-cli": "Gemini CLI", cursor: "Cursor", codex: "Codex", vscode: "VS Code", grok: "Grok",
};
/** an app in the product's words; null for `other`, an unknown id or nothing (a part we cannot name is left out) */
export function prettyApp(id: unknown): string | null {
  return typeof id === "string" && Object.hasOwn(APP_WORDS, id) ? APP_WORDS[id as keyof typeof APP_WORDS] : null;
}

/** The handshake's client names we know (exact, trimmed, case-insensitive): Claude Code names itself `claude-code`, Anthropic's connector
 *  `claude-ai`; the other agents' names are the coordinator's table (DECISIONS "2026-10-05 — Your own AI for every agent": extend it from
 *  the names real clients send). A near miss is `other`, never a guess. */
const CLIENT_NAMES: Record<string, AiApp> = {
  "claude-code": "claude-code", "claude-ai": "claude",
  codex: "codex", "gemini-cli": "gemini-cli", gemini: "gemini-cli", "cursor-vscode": "cursor", cursor: "cursor",
  "visual studio code": "vscode", vscode: "vscode", chatgpt: "chatgpt", openai: "chatgpt",
};
export function appOfClientName(name: string): AiApp {
  const n = name.trim().toLowerCase();
  return Object.hasOwn(CLIENT_NAMES, n) ? CLIENT_NAMES[n]! : "other";
}
/** The sign-in's client document → the app, at the connection's creation; a registered client (a random id) proves nothing: null. Exact
 *  addresses on the three hosts we fetch documents from, and the per-server forms of Codex's and ChatGPT's (Codex's first: its shared
 *  document sits under ChatGPT's per-connection pattern). */
const CLIENT_DOCUMENT_APPS: Record<string, AiApp> = {
  "https://claude.ai/oauth/mcp-oauth-client-metadata": "claude", // Claude on the web, the desktop app and Cowork alike
  "https://claude.ai/oauth/claude-code-client-metadata": "claude-code",
  "https://chatgpt.com/oauth/client.json": "chatgpt",
  "https://chatgpt.com/oauth/codex/client.json": "codex",
  "https://vscode.dev/oauth/client-metadata.json": "vscode",
};
const CLIENT_DOCUMENT_FORMS: [RegExp, AiApp][] = [
  [/^https:\/\/chatgpt\.com\/oauth\/codex\/[A-Za-z0-9_-]+\/client\.json$/, "codex"],
  [/^https:\/\/chatgpt\.com\/oauth\/[A-Za-z0-9_-]+\/client\.json$/, "chatgpt"],
];
export function appOfClientDocument(clientId: string | null | undefined): AiApp | null {
  if (!clientId) return null;
  if (Object.hasOwn(CLIENT_DOCUMENT_APPS, clientId)) return CLIENT_DOCUMENT_APPS[clientId]!;
  return CLIENT_DOCUMENT_FORMS.find(([form]) => form.test(clientId))?.[1] ?? null;
}
/** the handshake's app version is kept as sent, at most this long */
export const AI_APP_VERSION_MAX = 60;

/** The model as the AI reports it: one line of an id, at most 60 characters, letters, digits, spaces and `._:-[]` only (the AI's own word,
 *  shown to the team as plain text; nothing else can ride along). Trimmed; blank is refused. */
export const AI_MODEL_MAX = 60;
export const AI_MODEL_FORM = /^[A-Za-z0-9 ._:\-[\]]+$/;
export const aiModelField = () => z.string().trim().min(1).max(AI_MODEL_MAX).regex(AI_MODEL_FORM, "One line of a model id: letters, digits, spaces and . _ : - [ ]")
  .optional().describe("The model you are running as, by its id (for example claude-opus-5-5); leave it out when you do not know");

const FAMILIES: Record<string, string> = { opus: "Opus", sonnet: "Sonnet", haiku: "Haiku" };
/** claude-opus-5-5, claude-haiku-4-5-20251001 (a date suffix), claude-opus-5 */
const NEW_FORM = /^claude-(opus|sonnet|haiku)-(\d{1,2})(?:-(\d{1,2}))?(?:-\d{8})?$/i;
/** the older order: claude-3-5-sonnet-20241022 */
const OLD_FORM = /^claude-(\d{1,2})(?:-(\d{1,2}))?-(opus|sonnet|haiku)(?:-\d{8})?$/i;
const words = (family: string, major: string, minor: string | undefined) => `${FAMILIES[family.toLowerCase()]} ${major}${minor ? `.${minor}` : ""}`;
/** A model id in the founder's words ("Opus 5.5", "Haiku 4.5"); an id it does not know is shown as given (trimmed); nothing gives null. */
export function prettyModel(id: unknown): string | null {
  if (typeof id !== "string" || !id.trim()) return null;
  const s = id.trim();
  const n = NEW_FORM.exec(s);
  if (n) return words(n[1]!, n[2]!, n[3]);
  const o = OLD_FORM.exec(s);
  return o ? words(o[3]!, o[1]!, o[2]) : s;
}

/** The app and the model as one line ("Claude Code · Opus 5.5"); null when neither is known. */
function appModelLine(o: { app?: string | null; model?: string | null }): string | null {
  return [prettyApp(o.app), prettyModel(o.model)].filter(Boolean).join(" · ") || null;
}
/** The member's AI with the app and the model it reported: "Kriesi's AI · Claude Code · Opus 5.5"; the parts not known are left out. */
export function aiMark(o: { name: string; app?: string | null; model?: string | null }): string {
  const line = appModelLine(o);
  return line ? `${aiNameOf(o.name)} · ${line}` : aiNameOf(o.name);
}
/** The ticket page and marks round (2026-10-23): a byline names the app a member's AI worked through, "Kriesi · via Claude MCP". The
 *  Claude apps (the web, the desktop app, Cowork, or the hosted family when we cannot tell) read "Claude MCP"; the others their product
 *  names; an app that never named itself "an AI app" (never blank, never a guess). */
const CLAUDE_HOSTED: readonly string[] = ["claude", "claude-web", "claude-desktop", "cowork"];
export function viaAppLabel(app: unknown): string {
  if (typeof app === "string" && CLAUDE_HOSTED.includes(app)) return "Claude MCP";
  return prettyApp(app) ?? "an AI app";
}
/** the hover on the app's name in a byline: "Opus 5.5, as reported by the AI"; null when the AI reported no model */
export function modelHover(model: unknown): string | null {
  const words = prettyModel(model);
  return words ? `${words}, as reported by the AI` : null;
}
/** The kit icon of an app's mark (bundled glyphs, never fetched): the Claude family Claude's, ChatGPT and Codex OpenAI's, VS Code the kit's
 *  code sign, Grok and any other app the globe; null for nothing or a word we do not know. */
const APP_ICONS: Record<AiApp, string> = {
  claude: "claude", "claude-web": "claude", "claude-desktop": "claude", cowork: "claude", "claude-code": "claude", chatgpt: "openai", codex: "openai",
  "gemini-cli": "gemini", cursor: "cursor", vscode: "code", grok: "web", other: "web",
};
export function aiAppIcon(app: unknown): string | null {
  return typeof app === "string" && Object.hasOwn(APP_ICONS, app) ? APP_ICONS[app as AiApp] : null;
}

/** the small hint beside a mark whose model came from the AI */
export const MODEL_HINT = "The model as reported by the AI";
